import { useMemo } from 'react';
import { useAllUsers } from 'pos-client/hooks/useAllUsers.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import type { LocationModel } from 'pos-shared/models/locationSchema.ts';

type Props = {
  location: LocationModel;
};

export const useAssignedUsers = ({ location }: Props) => {
  const { users: usersList, isLoadingUsers: isGetAllUsersLoading } =
    useAllUsers();

  const { isLoading: isLinkUserLoading } =
    trpc.locations.linkUserToLocation.useMutation();

  const { data: assignedUserList, isFetching: isUserListLoading } =
    trpc.locations.findLocationLinkedUsers.useQuery({
      id: location.id,
    });

  const userEmail = (email: string | undefined) => (email ? `(${email})` : '');

  const usersToAssign = useMemo(() => {
    return (
      usersList?.results
        ?.filter((user) => !assignedUserList?.some((u) => u.id === user.id))
        .filter((user) => user?.status?.toLowerCase() !== 'inactive')
        .map((user) => {
          return {
            id: user.id as string,

            name: `${user.firstName} ${user.lastName} ${userEmail(
              user.userEmail
            )}`,
          };
        }) ?? []
    );
  }, [usersList, assignedUserList]);

  const locationUsersTableData = useMemo(() => {
    return (
      usersList?.results
        ?.filter((user) => assignedUserList?.some((u) => u.id === user.id))
        .map((user) => {
          return {
            ...user,
          };
        }) ?? []
    );
  }, [assignedUserList, usersList]);

  const formattedUsers = useMemo(() => {
    return usersToAssign.map((user) => ({
      label: user.name,
      value: user.id,
    }));
  }, [usersToAssign]);

  return {
    isLoading: isGetAllUsersLoading || isLinkUserLoading || isUserListLoading,
    usersList,
    assignedUsers: assignedUserList,
    locationUsersTableData: locationUsersTableData,
    dropDownUsers: formattedUsers,
  };
};
